import { cursorPointer } from '@/component/NaviTop/style.css'
import PageTransitionWrapper from '@/component/PageTransitionWrapper'
import { RandomColorBox } from '@/component/RandomColorBox'
import { fetchIndustries, type IndustryCategory } from '@/util/ref'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { fetchCompanies, getPerm, setBookmark, buildSearchParams, setActive } from './data'
import type { Company, CompanyFilter, Perm, ActivenessPayload, SetBookmarkPayload } from './data'
import { bg } from './style.css'
import {
  SparklesIcon,
  ListCheckIcon,
  Building2Icon,
  MousePointerClickIcon,
  CheckLineIcon,
} from 'lucide-react'
import Form from 'react-bootstrap/Form'

export default function CompanyList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [filter, setFilter] = useState<CompanyFilter>({
    // pageNo: 1,
    // pageSize: 200,
    bookmarked: false,
    industryCategoryId: undefined,
    ...Object.fromEntries(
      Array.from(searchParams.entries()).map(([key, value]) => {
        if (value === 'true') return [key, true]
        if (value === 'false') return [key, false]
        if (!isNaN(Number(value))) return [key, Number(value)]
        return [key, value]
      })
    ),
  })
  const { data: permission } = useQuery<Perm>({
    queryKey: ['company', 'perm'],
    queryFn: getPerm,
    staleTime: Infinity,
  })
  const {
    data: industryCategories,
    isLoading: loadingIndustries,
    isError: industriesError,
  } = useQuery<IndustryCategory[]>({
    queryKey: ['industry'],
    queryFn: fetchIndustries,
    staleTime: 1000 * 60 * 5,
  })
  const {
    data: companies,
    // isLoading: loadingCompanies,
    isError: companiesError,
  } = useQuery<Company[], Error>({
    queryKey: ['company', filter],
    queryFn: () => {
      setSearchParams(buildSearchParams(filter))
      return fetchCompanies(filter)
    },
    staleTime: 0,
    refetchOnMount: true,
  })

  const companyActivenessMutation = useMutation({
    mutationFn: (payload: ActivenessPayload) => setActive(payload),
    onMutate: ({ id, isActive }: ActivenessPayload) => {
      queryClient.setQueryData(['company', filter], (companies: Company[]) =>
        (companies ?? []).map((company) => (company.id === id ? { ...company, isActive } : company))
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['company', filter] })
    },
  })

  const bookmarkMutation = useMutation({
    mutationFn: (payload: SetBookmarkPayload) => setBookmark(payload),
    onMutate: ({ companyId, action }: SetBookmarkPayload) => {
      queryClient.setQueryData(['company', filter], (companies: Company[]) =>
        (companies ?? []).map((company) =>
          company.id === companyId ? { ...company, bookmarked: action === 'ADD' } : company
        )
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['company', filter] })
    },
  })

  const selectedIndustryCategory = industryCategories?.find(
    (c) => c.id === filter.industryCategoryId
  )

  const queryClient = useQueryClient()

  useEffect(() => {
    if (companies) {
      const bookmarkedCompanies = []
      for (const company of companies) {
        queryClient.setQueryData(['company', company.id], company)
        if (company.bookmarked) bookmarkedCompanies.push(company)
      }
      queryClient.setQueryData(['company', 'bookmarked'], bookmarkedCompanies)
    }
  }, [companies, queryClient, filter, setSearchParams])

  return (
    <>
      <Card>
        <Card.Body className="d-flex flex-row justify-content-between">
          <div className="d-flex flex-row justify-content-center align-items-center">
            <Building2Icon className={`${bg} me-2`} />
            <div>
              <Card.Title>興味ある企業数</Card.Title>
              <Card.Subtitle className="mb-2 text-muted">
                現在
                {companies
                  ? companies.reduce((sum, company) => sum + (company.bookmarked ? 1 : 0), 0)
                  : 0}
                社 をマーク中
              </Card.Subtitle>
            </div>
          </div>
          <Button
            variant="outline-secondary"
            className="d-flex justify-content-center align-items-center"
            onClick={() => navigate('/user/profile/bookmarked-company')}
          >
            <ListCheckIcon className="me-1" />
            リストを見る
          </Button>
        </Card.Body>
      </Card>
      <Card className="my-4">
        <Card.Body>
          <Card.Title className="d-flex flex-row align-items-center">
            <SparklesIcon className="me-1" />
            企業を探す
          </Card.Title>
          <Card.Subtitle className="mb-2 text-muted">興味のある分野をタップ</Card.Subtitle>
          {loadingIndustries ? (
            <Spinner />
          ) : industriesError ? (
            <Alert />
          ) : (
            industryCategories?.map(({ id, name, emoji }, i) => (
              <RandomColorBox
                key={id}
                index={i}
                active={id === filter.industryCategoryId}
                onClick={() =>
                  setFilter({
                    ...filter,
                    industryCategoryId: id === filter.industryCategoryId ? undefined : id,
                  })
                }
              >
                {emoji} {name}
              </RandomColorBox>
            ))
          )}
          <Card.Subtitle className="my-2 text-muted d-flex justify-content-center">
            <div>
              {selectedIndustryCategory
                ? `選択中：${selectedIndustryCategory.emoji} ${selectedIndustryCategory.name}`
                : 'フィルターを選択すると、該当する企業のみが表示されます'}
            </div>
          </Card.Subtitle>
        </Card.Body>
      </Card>
      {permission?.canCreate && (
        <Button onClick={() => navigate('/company/new')}>Add Company</Button>
      )}
      <PageTransitionWrapper>
        {companiesError ? (
          <Alert variant="danger">error</Alert>
        ) : (
          companies?.map((company) => (
            <Card
              key={company.id}
              className={`my-4 ${cursorPointer}`}
              onClick={() => navigate({ pathname: `/company/${company.id}/view`, search: '' })}
            >
              <Card.Body>
                <Card.Title className="d-flex justify-content-between">
                  {company.name}
                  {permission?.canReadInactive && (
                    <Form.Check
                      type="switch"
                      disabled={!permission.canWrite}
                      checked={company.isActive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        companyActivenessMutation.mutate({
                          id: company.id,
                          isActive: e.target.checked,
                        })
                      }}
                      className={cursorPointer}
                    />
                  )}
                </Card.Title>
                <div>
                  <ul>
                    {company.summary?.map((summaryItem, idx) => (
                      <li key={idx} className="text-muted">
                        {summaryItem}
                      </li>
                    ))}
                  </ul>
                </div>
                <Stack direction="horizontal" gap={2} className="mb-2 flex-wrap">
                  {company.industries.map((industry) => (
                    <Badge bg="secondary" key={industry.id}>
                      {industry.name}
                    </Badge>
                  ))}
                </Stack>
                <Card.Footer className="d-flex justify-content-center">
                  <Button
                    variant={company.bookmarked ? 'primary' : 'outline-secondary'}
                    className="d-flex align-items-center"
                    onClick={(e) => {
                      e.stopPropagation()
                      bookmarkMutation.mutate({
                        companyId: company.id,
                        action: company.bookmarked ? 'REMOVE' : 'ADD',
                      })
                    }}
                  >
                    {company.bookmarked ? (
                      <>
                        <CheckLineIcon className="me-1" />
                        チェック済み
                      </>
                    ) : (
                      <>
                        <MousePointerClickIcon className="me-1" />
                        興味あり
                      </>
                    )}
                  </Button>
                </Card.Footer>
              </Card.Body>
            </Card>
          ))
        )}
      </PageTransitionWrapper>
    </>
  )
}
